import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/layout/AdminLayout';
import MapPickerDialog from '../../components/MapPickerDialog';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { MapPin, Pencil, Trash2, Plus, Search, Loader2, ExternalLink, Save } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { site_name: '', postcode: '', location_link: '', geo_radius: '' };

export default function SavedSitesPage() {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = add new
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);

  const fetchSites = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/retail-sites`);
      setSites(r.data || []);
    } catch { toast.error('Failed to load saved sites'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchSites(); }, [fetchSites]);

  const openAdd = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      site_name: s.site_name || '',
      postcode: s.postcode || '',
      location_link: s.location_link || '',
      geo_radius: s.geo_radius != null ? String(s.geo_radius) : '',
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.site_name.trim()) { toast.error('Site name is required'); return; }
    setSaving(true);
    const payload = {
      site_name: form.site_name.trim(),
      postcode: form.postcode.trim(),
      location_link: form.location_link.trim() || null,
      geo_radius: form.location_link.trim() && form.geo_radius ? parseInt(form.geo_radius) : null,
    };
    try {
      if (editing) await axios.put(`${API}/admin/retail-sites/${editing.id}`, payload);
      else await axios.post(`${API}/admin/retail-sites/upsert`, payload);
      toast.success(editing ? 'Site updated' : 'Site saved');
      setDialogOpen(false);
      fetchSites();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete saved site "${s.site_name}"? Existing shifts are NOT affected — only the autofill entry is removed.`)) return;
    try {
      await axios.delete(`${API}/admin/retail-sites/${s.id}`);
      toast.success('Site deleted');
      fetchSites();
    } catch { toast.error('Delete failed'); }
  };

  const filtered = sites.filter((s) =>
    !q.trim() ||
    (s.site_name || '').toLowerCase().includes(q.toLowerCase()) ||
    (s.postcode || '').toLowerCase().includes(q.toLowerCase())
  );

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 space-y-4 max-w-3xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-barlow text-xl sm:text-2xl uppercase tracking-wider text-foreground flex items-center gap-2">
              <MapPin className="w-5 h-5 text-emerald-400" /> Saved Sites
            </h1>
            <p className="text-sm text-muted-foreground">
              Sites remembered by the shift forms — name, postcode, clock-in point and allowed distance autofill when you pick them.
            </p>
          </div>
          <Button onClick={openAdd} className="rounded-sm bg-primary text-primary-foreground" data-testid="add-site-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Add Site
          </Button>
        </div>

        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or postcode…"
            className="pl-9 bg-card border-border rounded-sm"
            data-testid="sites-search-input"
          />
        </div>

        {loading ? (
          <div className="py-16 text-center text-muted-foreground animate-pulse">Loading…</div>
        ) : filtered.length === 0 ? (
          <Card className="border-border"><CardContent className="py-12 text-center text-sm text-muted-foreground">
            {q ? 'No saved sites match your search.' : 'No saved sites yet — they are saved automatically when you create shifts, or add one manually.'}
          </CardContent></Card>
        ) : (
          <div className="space-y-2">
            {filtered.map((s) => (
              <Card key={s.id} className="border-border" data-testid={`site-row-${s.id}`}>
                <CardContent className="p-3 flex flex-wrap items-center gap-2">
                  <div className="min-w-0 flex-1 basis-full sm:basis-auto">
                    <p className="text-sm font-medium truncate">{s.site_name}</p>
                    <p className="text-[11px] text-muted-foreground">
                      {s.postcode || 'No postcode'}
                      {s.updated_at ? ` · updated ${new Date(s.updated_at).toLocaleDateString('en-GB')}` : ''}
                    </p>
                  </div>
                  {s.location_link ? (
                    <Badge className="rounded-sm text-[10px] uppercase bg-emerald-500/15 text-emerald-400 border-emerald-500/40 flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> {s.geo_radius ? `${s.geo_radius} m` : 'No limit'}
                    </Badge>
                  ) : (
                    <Badge className="rounded-sm text-[10px] uppercase bg-muted text-muted-foreground border-border">No point</Badge>
                  )}
                  {s.location_link && (
                    <Button size="sm" variant="ghost" asChild className="h-7 px-2" data-testid={`site-open-link-${s.id}`}>
                      <a href={s.location_link} target="_blank" rel="noreferrer" title="Open in Google Maps">
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => openEdit(s)} className="h-7 px-2 rounded-sm text-[11px]" data-testid={`site-edit-${s.id}`}>
                    <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                  </Button>
                  <Button size="sm" onClick={() => remove(s)} className="h-7 px-2 rounded-sm text-[11px] bg-red-600 hover:bg-red-500 text-white" data-testid={`site-delete-${s.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-card border-border rounded-sm max-w-md" data-testid="site-edit-dialog">
            <DialogHeader>
              <DialogTitle className="font-barlow uppercase tracking-wider text-base">
                {editing ? 'Edit Saved Site' : 'Add Saved Site'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Site name</Label>
                <Input value={form.site_name} onChange={(e) => setForm({ ...form, site_name: e.target.value })}
                  placeholder="e.g., Eldon Garden Newcastle" className="bg-background border-border rounded-sm" data-testid="site-form-name" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Postcode</Label>
                <Input value={form.postcode} onChange={(e) => setForm({ ...form, postcode: e.target.value })}
                  placeholder="e.g., NE1 7RA" className="bg-background border-border rounded-sm" data-testid="site-form-postcode" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Clock-in location link</Label>
                <Input value={form.location_link} onChange={(e) => setForm({ ...form, location_link: e.target.value })}
                  placeholder="Paste Google Maps / what3words link" className="bg-background border-border rounded-sm" data-testid="site-form-link" />
                <Button type="button" variant="outline" size="sm" onClick={() => setMapOpen(true)}
                  className="h-8 rounded-sm text-xs w-full sm:w-auto" data-testid="site-form-map-btn">
                  <MapPin className="w-3.5 h-3.5 mr-1.5 text-emerald-400" /> Pick on map
                </Button>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Allowed distance</Label>
                <select value={form.geo_radius} onChange={(e) => setForm({ ...form, geo_radius: e.target.value })}
                  className="w-full h-9 bg-background border border-border rounded-sm px-2 text-sm" data-testid="site-form-radius">
                  <option value="">No limit</option>
                  <option value="25">25 m</option>
                  <option value="50">50 m</option>
                  <option value="100">100 m</option>
                  <option value="200">200 m</option>
                  <option value="500">500 m</option>
                </select>
              </div>
              <Button onClick={save} disabled={saving} className="w-full rounded-sm bg-primary text-primary-foreground" data-testid="site-form-save">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> {editing ? 'Save changes' : 'Add site'}</>}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <MapPickerDialog
          open={mapOpen}
          onOpenChange={setMapOpen}
          initialLink={form.location_link}
          postcode={form.postcode}
          siteName={form.site_name}
          onPick={(link) => setForm((f) => ({ ...f, location_link: link }))}
        />
      </div>
    </AdminLayout>
  );
}
